import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { captureError } from '@shared/lib/observability';
import { getPushState, enablePush, disablePush, type PushState } from '@shared/push';
import { useCreator } from './useCreator';
import { VerifiedBadge } from '@shared/ui';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import { BrandContact } from './BrandContact';
import { IgLink } from '@shared/ui/IgLink';

// Creator Demand feed (job-board model): OPEN campaigns businesses posted, ranked
// by niche match. Tap a card → full brief (Coconi-style) → apply with a pitch +
// optional proposed price. Reads get_creator_campaign_feed(); writes apply_to_campaign().

interface FeedRow {
  id: string;
  title: string;
  brand_id: string | null;
  brand_name: string | null;
  brand_logo_url: string | null;
  category_id: string | null;
  category_ru: string | null;
  category_en: string | null;
  compensation_type: 'paid' | 'barter' | 'both';
  budget: number | null;
  budget_basis: 'per_creator' | 'total';
  currency: string;
  max_creators: number;
  apply_deadline: string | null;
  niche_ids: string[] | null;
  cover_image_url: string | null;
  cover_focus_y: number | null;
  niche_match: number;
  applicant_count: number;
  already_applied: boolean;
  published_at: string | null;
}

interface CampaignFull {
  id: string;
  title: string;
  description: string | null;
  requirements: string | null;
  nice_to_have: string | null;
  cover_image_url: string | null;
  cover_focus_y: number | null;
  compensation_type: 'paid' | 'barter' | 'both';
  budget: number | null;
  budget_basis: 'per_creator' | 'total';
  currency: string;
  barter_description: string | null;
  max_creators: number;
  apply_deadline: string | null;
  content_deadline: string | null;
  audience_geo: string | null;
  audience_age_min: number | null;
  audience_age_max: number | null;
  min_followers: number | null;
  min_engagement_rate: number | null;
  content_language: 'ru' | 'ky' | 'both' | null;
  on_site_required: boolean;
  on_site_location: string | null;
  usage_rights: 'organic' | 'paid_ads' | 'whitelisting';
  require_vertical: boolean;
  require_hd: boolean;
  no_competitor_logos: boolean;
  must_tag_brand: boolean;
  brand: { name: string | null; logo_url: string | null } | { name: string | null; logo_url: string | null }[] | null;
  category: { name_ru: string | null; name_en: string | null } | { name_ru: string | null; name_en: string | null }[] | null;
  deliverables: { platform: string; quantity: number; notes: string | null; sort_order: number }[] | null;
}

function one<T>(v: T | T[] | null | undefined): T | null {
  if (!v) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}
const fmtMoney = (n: number | null | undefined, cur = 'KGS') =>
  n == null ? null : `${n.toLocaleString('ru-RU')} ${cur === 'KGS' ? 'сом' : cur}`;
const fmtK = (n: number | null | undefined) =>
  n == null ? null : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

const PLATFORM_RU: Record<string, string> = {
  instagram_reel: 'Reels', instagram_story: 'Stories', instagram_post: 'Пост в ленте',
  instagram_video: 'Видео IG', tiktok_video: 'TikTok видео', tiktok_photo: 'TikTok фото',
  youtube_short: 'YouTube Shorts', other: 'Другое',
};

// ── "Brands to pitch" — the original cold-outreach list: brands active in your
// niche you don't work with yet, + the peer creators they DO work with. ─────
interface PitchRow { brand_id: string; brand_name: string; peers: number; handle: string | null; avatar: string | null; can_message: boolean; verified: boolean; subcategory: string | null; subcategory_id: string | null }
interface Peer { handle: string; followers: number | null }

function BrandPeers({ brandId }: { brandId: string }) {
  const { t } = useTranslation('provider');
  const q = useSupabaseQuery<Peer[]>(['brand-peers', brandId], async () =>
    supabase.rpc('creator_pitch_brand_peers', { p_brand_id: brandId }));
  const verif = useHandleVerification((q.data ?? []).map((p) => p.handle));
  if (q.isLoading) return <div style={{ padding: '0 14px 12px', color: 'var(--dim)', fontSize: 12 }}>…</div>;
  if (!q.data?.length) return null;
  return (
    <div style={{ padding: '2px 14px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {q.data.map((p) => (
        <a key={p.handle} href={`https://instagram.com/${p.handle}`} target="_blank" rel="noreferrer"
          style={{ textDecoration: 'none', fontSize: 12, color: 'var(--accent-text)', background: 'rgba(74,158,255,0.10)', border: '1px solid rgba(74,158,255,0.22)', borderRadius: 16, padding: '4px 9px' }}>
          @{p.handle}<VerifiedBadge {...verif(p.handle)} size={13} /> <span style={{ color: 'var(--dim)' }}>{fmtK(p.followers)}</span>
        </a>
      ))}
      <span style={{ width: '100%', color: 'var(--dim)', fontSize: 11, marginTop: 2 }}>{t('creator.demand.peersHint', { defaultValue: 'Блогеры вашей ниши, которые уже работали с брендом.' })}</span>
    </div>
  );
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}

function BrandsToPitch() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState<{ id: string; name: string } | null>(null);
  const dq = useDebounced(query, 200);
  const filtering = !!cat || dq.trim().length >= 2;
  const pitchQ = useSupabaseQuery<PitchRow[]>(['creator-pitch'], async () => supabase.rpc('creator_pitch_brands', { p_limit: 24 }));
  const searchQ = useSupabaseQuery<PitchRow[]>(['brand-search', cat?.id ?? dq.trim()],
    async () => supabase.rpc('creator_search_brands', { p_query: cat ? '' : dq.trim(), p_subcategory: cat?.id ?? null, p_limit: 24 }),
    { enabled: filtering });
  const [open, setOpen] = useState<string | null>(null);

  const pickCat = (id: string | null, name: string | null) => { if (!id) return; setCat({ id, name: name ?? '' }); setQuery(''); setOpen(null); };
  const onType = (v: string) => { setQuery(v); if (v.trim()) setCat(null); };

  const catChip: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)', color: '#9fc0ff', borderRadius: 999, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const brandRow = (r: PitchRow) => {
    const isOpen = open === r.brand_id;
    return (
      <div key={r.brand_id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '11px 14px 0' }}>
          <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.brand_name}</span>
            {r.verified && <span title="Verified" style={{ color: 'var(--accent)', fontSize: 13, flex: 'none' }}>✔︎</span>}
            {r.handle && <IgLink handle={r.handle} size={16} />}
          </span>
          <span style={{ flexShrink: 0 }}>
            <BrandContact compact brand={{ brand_id: r.brand_id, name: r.brand_name, handle: r.handle, can_message: r.can_message }} />
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 14px 11px' }}>
          {r.subcategory
            ? <button type="button" onClick={() => pickCat(r.subcategory_id, r.subcategory)} style={catChip} title={tr('creator.demand.catFilter', 'Показать бренды категории') as string}>{r.subcategory}</button>
            : <span />}
          <button type="button" onClick={() => setOpen(isOpen ? null : r.brand_id)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#8bd450', fontSize: 12, whiteSpace: 'nowrap', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
            {r.peers > 0 && tr('creator.demand.peers', '{{n}} креаторов', { n: r.peers })}
            <span style={{ color: 'var(--dim)', fontSize: 10 }}>{isOpen ? '▲' : '▼'}</span>
          </button>
        </div>
        {isOpen && <BrandPeers brandId={r.brand_id} />}
      </div>
    );
  };

  const rows = filtering ? searchQ.data : pitchQ.data;
  const loading = filtering ? searchQ.isLoading : pitchQ.isLoading;

  return (
    <>
      <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 10px' }}>
        {tr('creator.demand.pitchSub', 'Эти бренды активно рекламируются и работают с блогерами вашей ниши — а с вами ещё нет. Хорошие кандидаты, чтобы написать первым.')}
      </p>
      <input value={query} onChange={(e) => onType(e.target.value)} type="search" aria-label={tr('creator.demand.searchBrand', 'Поиск бренда по Instagram или названию…') as string}
        placeholder={tr('creator.demand.searchBrand', 'Поиск бренда по Instagram или названию…') as string}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.25)', color: 'var(--text)', border: '1px solid rgba(74,158,255,0.25)', fontSize: 14, fontFamily: 'inherit', marginBottom: cat ? 8 : 12 }} />
      {cat && (
        <div style={{ marginBottom: 12 }}>
          <button type="button" onClick={() => setCat(null)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(74,158,255,0.14)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 999, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            {tr('creator.demand.category', 'Категория')}: {cat.name} <span style={{ fontSize: 13 }}>✕</span>
          </button>
        </div>
      )}
      {loading ? <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>…</div>
        : !rows?.length ? <div style={{ color: 'var(--dim)', fontSize: 13, padding: 20 }}>{filtering ? tr('creator.demand.searchEmpty', 'Бренды не найдены.') : tr('creator.demand.pitchEmpty', 'Пока нет подходящих брендов.')}</div>
        : <div style={{ display: 'grid', gap: 8 }}>{rows.map(brandRow)}</div>}
    </>
  );
}

// Opt-in toggle for Web Push notifications about new matching campaigns.
function PushToggle({ providerId }: { providerId: string | null }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [state, setState] = useState<PushState | 'busy'>('unsubscribed');
  useEffect(() => { let on = true; getPushState().then((s) => on && setState(s)); return () => { on = false; }; }, []);
  if (state === 'unsupported') return null;
  const enable = async () => { setState('busy'); const r = await enablePush(providerId); setState(r.ok ? 'subscribed' : r.error === 'denied' ? 'denied' : 'unsubscribed'); };
  const disable = async () => { setState('busy'); await disablePush(); setState('unsubscribed'); };
  const btn: React.CSSProperties = { border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.22)', borderRadius: 10, padding: '9px 12px', marginBottom: 12 }}>
      <span style={{ color: 'var(--accent-text)', fontSize: 12.5 }}>{tr('creator.demand.notify', 'Уведомлять о новых заданиях')}</span>
      {state === 'denied' ? (
        <span style={{ color: 'var(--dim)', fontSize: 11.5 }}>{tr('creator.demand.notifyBlocked', 'разрешите в настройках браузера')}</span>
      ) : state === 'subscribed' ? (
        <button onClick={disable} style={{ ...btn, background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--muted)' }}>{tr('creator.demand.notifyOff', 'Выключить')}</button>
      ) : (
        <button onClick={enable} disabled={state === 'busy'} style={{ ...btn, background: 'rgba(74,158,255,0.2)', color: 'var(--accent-text)' }}>{state === 'busy' ? '…' : tr('creator.demand.notifyOn', 'Включить')}</button>
      )}
    </div>
  );
}

// ── "С рынка" — gigs aggregated from public Telegram channels (external_jobs).
// Aggregator model: apply opens the poster's own contact (or the source post),
// and we log the click. We never hide the source. ─────────────────────────────
interface MarketJob {
  id: string; title: string; brand_name: string | null; description: string | null;
  offer_type: string | null; offer_detail: string | null; location: string[] | null;
  requirements: string | null; contact_type: string | null; contact_value: string | null;
  deadline: string | null; source_url: string | null; source_channel: string | null; posted_at: string | null;
}

// Turn a free-form KG contact into a tappable URL; fall back to the source post.
function contactHref(j: MarketJob): string {
  const v = (j.contact_value || '').trim();
  const ct = j.contact_type;
  if (v) {
    if (ct === 'instagram') return `https://instagram.com/${v.replace(/^@/, '').split(/[\s,]/)[0]}`;
    if (ct === 'telegram') return /^https?:/.test(v) ? v : `https://t.me/${v.replace(/^@/, '')}`;
    if (ct === 'whatsapp' || ct === 'phone') {
      let d = v.replace(/\D/g, '');
      if (d.length === 10 && d.startsWith('0')) d = '996' + d.slice(1);   // 0705… → 996705…
      else if (d.length === 9) d = '996' + d;                              // 705… → 996705…
      return ct === 'whatsapp' ? `https://wa.me/${d}` : `tel:+${d}`;
    }
    if (/^https?:/.test(v)) return v;
  }
  return '#'; // never fall back to the source channel; feed only shows jobs with a contact
}

function MarketJobs() {
  const { t, i18n } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const q = useSupabaseQuery<MarketJob[]>(['creator-external-jobs'], async () =>
    supabase.rpc('creator_external_jobs', {}));
  const fmtDate = (d: string | null) =>
    !d ? null : new Intl.DateTimeFormat(i18n.language || 'ru', { day: 'numeric', month: 'long' }).format(new Date(d));
  const offerLabel = (j: MarketJob) => {
    const base = j.offer_type === 'paid' ? tr('creator.demand.offerPaid', 'Оплата')
      : j.offer_type === 'barter' ? tr('creator.demand.barter', 'Бартер')
      : j.offer_type === 'contest' ? tr('creator.demand.offerContest', 'Конкурс') : null;
    return [base, j.offer_detail].filter(Boolean).join(': ');
  };
  const apply = (j: MarketJob) => {
    supabase.rpc('creator_log_external_job_click', { p_job_id: j.id }).then(() => {}, () => {}); // fire-and-forget
    window.open(contactHref(j), '_blank', 'noopener,noreferrer');
  };
  const rows = q.data ?? [];
  const meta: React.CSSProperties = { color: 'var(--muted)', fontSize: 12 };
  return (
    <>
      <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 12px' }}>
        {tr('creator.demand.marketSub', 'Задания от брендов и мастеров, собранные из открытых источников. Откликайтесь напрямую у заказчика.')}
      </p>
      {q.isLoading ? (
        <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13, padding: 20, textAlign: 'center' }}>
          {tr('creator.demand.marketEmpty', 'Пока нет заданий с рынка. Загляните позже.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((j) => (
            <div key={j.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15, lineHeight: 1.25 }}>{j.title}</div>
              {j.brand_name && <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 4 }}>{j.brand_name}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 7 }}>
                {offerLabel(j) && <span style={{ color: 'var(--text-2)', fontSize: 12.5 }}>{offerLabel(j)}</span>}
                {j.location?.length ? <span style={meta}>{j.location.join(', ')}</span> : null}
                {j.deadline && <span style={meta}>{tr('creator.demand.until', 'до')} {fmtDate(j.deadline)}</span>}
              </div>
              {j.requirements && (
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 7 }}>{j.requirements}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 11 }}>
                <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                  {tr('creator.demand.marketVia', 'С рынка')}{j.posted_at ? ` · ${fmtDate(j.posted_at)}` : ''}
                </span>
                <button type="button" onClick={() => apply(j)}
                  style={{ background: 'linear-gradient(90deg,#e0568f,#c94ea0)', border: 'none', color: '#fff', borderRadius: 9, padding: '10px 16px', minHeight: 44, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  {tr('creator.demand.respond', 'Откликнуться')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function CreatorDemand() {
  const { t, i18n } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const { provider } = useCreator();
  // The open campaign and the tab live in the address (?campaign=<id>, ?tab=pitch): the phone's
  // Back button then closes the campaign instead of leaving «Спрос», and a link opens it directly.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('campaign');
  const mode: 'campaigns' | 'pitch' | 'market' = params.get('tab') === 'pitch' ? 'pitch' : params.get('tab') === 'market' ? 'market' : 'campaigns';
  const setMode = (m: 'campaigns' | 'pitch' | 'market') => setParams((p) => { const n = new URLSearchParams(p); if (m === 'campaigns') n.delete('tab'); else n.set('tab', m); return n; }, { replace: true });
  const openCampaign = (id: string) => setParams((p) => { const n = new URLSearchParams(p); n.set('campaign', id); return n; });
  const closeCampaign = () => {
    // Opened from the list → step back in history; opened from a link → just drop the param.
    if ((window.history.state?.idx ?? 0) > 0) window.history.back();
    else setParams((p) => { const n = new URLSearchParams(p); n.delete('campaign'); return n; }, { replace: true });
  };

  const q = useSupabaseQuery<FeedRow[]>(['creator-campaign-feed'], async () =>
    supabase.rpc('get_creator_campaign_feed'));
  const selected = selectedId ? (q.data ?? []).find((r) => r.id === selectedId) ?? null : null;

  const fmtDate = (d: string | null) =>
    !d ? null : new Intl.DateTimeFormat(i18n.language || 'ru', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(d));

  const compLabel = (r: Pick<FeedRow, 'compensation_type' | 'budget' | 'budget_basis' | 'currency'>) => {
    if (r.compensation_type === 'barter') return tr('creator.demand.barter', 'Бартер');
    const money = fmtMoney(r.budget, r.currency);
    const basis = r.budget_basis === 'total'
      ? tr('creator.demand.basisTotal', 'общий бюджет')
      : tr('creator.demand.basisEach', 'за блогера');
    const suffix = r.compensation_type === 'both' ? ` + ${tr('creator.demand.barter', 'Бартер')}` : '';
    return money ? `${money} · ${basis}${suffix}` : tr('creator.demand.barter', 'Бартер');
  };

  if (selected) {
    return <CampaignDetail row={selected} onBack={closeCampaign} fmtDate={fmtDate} compLabel={compLabel} />;
  }

  const rows = q.data ?? [];

  return (
    <div className="cr-page">
      <h2 style={{ color: 'var(--text)', fontSize: 18, margin: '4px 0 8px' }}>{tr('creator.demand.title', 'Спрос')}</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {([['campaigns', tr('creator.demand.tabCampaigns', 'Задания')], ['market', tr('creator.demand.tabMarket', 'С рынка')], ['pitch', tr('creator.demand.tabPitch', 'Бренды для питча')]] as const).map(([m, lbl]) => (
          <button type="button" key={m} onClick={() => setMode(m)} aria-pressed={mode === m}
            style={{ padding: '7px 14px', minHeight: 44, borderRadius: 22, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
              border: `1px solid ${mode === m ? 'var(--accent)' : 'rgba(255,255,255,0.14)'}`,
              background: mode === m ? 'rgba(74,158,255,0.14)' : 'transparent',
              color: mode === m ? 'var(--accent-text)' : 'var(--muted)' }}>{lbl}</button>
        ))}
      </div>

      {mode === 'market' ? <MarketJobs /> : mode === 'pitch' ? <BrandsToPitch /> : (
      <>
      <PushToggle providerId={provider?.id ?? null} />
      <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 12px' }}>
        {tr('creator.demand.sub', 'Бренды ищут блогеров прямо сейчас. Откликнитесь на подходящие — сверху те, что ближе к вашей нише.')}
      </p>

      {q.isLoading ? (
        <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13, padding: 20, textAlign: 'center' }}>
          {tr('creator.demand.empty', 'Пока нет активных заданий. Загляните позже — новые появляются регулярно.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((r) => (
            <button type="button" key={r.id} onClick={() => openCampaign(r.id)}
              style={{ textAlign: 'left', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 0, cursor: 'pointer', fontFamily: 'inherit', overflow: 'hidden' }}>
              {r.cover_image_url && (
                <img src={r.cover_image_url} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', objectPosition: `50% ${r.cover_focus_y ?? 50}%`, display: 'block' }} />
              )}
              <div style={{ padding: '11px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15, lineHeight: 1.25 }}>{r.title}</div>
                  {(r.category_ru || r.category_en) && (
                    <span style={{ flexShrink: 0, color: '#f5a9c8', background: 'rgba(245,169,200,0.12)', borderRadius: 12, fontSize: 11, padding: '3px 9px', whiteSpace: 'nowrap' }}>
                      {i18n.language === 'en' ? (r.category_en || r.category_ru) : (r.category_ru || r.category_en)}
                    </span>
                  )}
                </div>
                {r.brand_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0 8px' }}>
                    {r.brand_logo_url && <img src={r.brand_logo_url} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />}
                    <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{r.brand_name}</span>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: 'var(--text-2)' }}>{compLabel(r)}</span>
                  {r.apply_deadline && <span>{tr('creator.demand.until', 'до')} {fmtDate(r.apply_deadline)}</span>}
                  <span>{t('creator.demand.need', { count: r.max_creators })}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
                  {r.niche_match > 0 && (
                    <span style={{ color: '#8bd450', background: 'rgba(139,212,80,0.12)', border: '1px solid rgba(139,212,80,0.3)', borderRadius: 12, fontSize: 11, padding: '3px 8px' }}>
                      {tr('creator.demand.match', 'Подходит вашей нише')}
                    </span>
                  )}
                  {r.already_applied ? (
                    <span style={{ color: '#6699ff', fontSize: 11.5, padding: '3px 2px' }}>{tr('creator.demand.applied', 'Вы откликнулись')}</span>
                  ) : (
                    <span style={{ color: 'var(--dim)', fontSize: 11.5, padding: '3px 2px' }}>
                      {tr('creator.demand.applicants', '{{n}} откликнулись', { n: r.applicant_count })}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── Full campaign brief + apply ────────────────────────────────────────────
function CampaignDetail({
  row, onBack, fmtDate, compLabel,
}: {
  row: FeedRow;
  onBack: () => void;
  fmtDate: (d: string | null) => string | null;
  compLabel: (r: Pick<FeedRow, 'compensation_type' | 'budget' | 'budget_basis' | 'currency'>) => string;
}) {
  const { t, i18n } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const invalidate = useInvalidateSupabase();
  const [showForm, setShowForm] = useState(false);
  const [pitch, setPitch] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [applied, setApplied] = useState(row.already_applied);
  const { verified, provider: me } = useCreator();
  const navigate = useNavigate();

  const q = useSupabaseQuery<CampaignFull>(['campaign-detail', row.id], async () =>
    supabase.from('creator_campaigns')
      .select('id,title,description,requirements,nice_to_have,cover_image_url,cover_focus_y,compensation_type,budget,budget_basis,currency,barter_description,max_creators,apply_deadline,content_deadline,audience_geo,audience_age_min,audience_age_max,min_followers,min_engagement_rate,content_language,on_site_required,on_site_location,usage_rights,require_vertical,require_hd,no_competitor_logos,must_tag_brand, brand:brands(name,logo_url), category:categories(name_ru,name_en), deliverables:campaign_deliverables(platform,quantity,notes,sort_order)')
      .eq('id', row.id).single());

  const c = q.data;
  const brand = one(c?.brand);
  const cat = one(c?.category);
  const dels = (c?.deliverables ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);

  const usageLabel = c && ({
    organic: tr('creator.demand.usageOrganic', 'Только органика'),
    paid_ads: tr('creator.demand.usagePaid', 'Можно в рекламу'),
    whitelisting: tr('creator.demand.usageWl', 'Whitelisting'),
  }[c.usage_rights]);
  const langLabel = c?.content_language && ({
    ru: 'RU', ky: 'KY', both: 'RU / KY',
  }[c.content_language]);

  const submit = async () => {
    setBusy(true); setErr(null);
    const p_price = price.trim() === '' ? null : Number(price);
    if (p_price != null && (Number.isNaN(p_price) || p_price < 0)) { setBusy(false); setErr(tr('creator.demand.badPrice', 'Проверьте цену.')); return; }
    const { error } = await supabase.rpc('apply_to_campaign', {
      p_campaign_id: row.id, p_pitch: pitch.trim() || null, p_price,
    });
    setBusy(false);
    if (error) { captureError(error, { where: 'creator.demand.apply', campaign: row.id }); setErr(
      error.message.includes('campaign not open') ? tr('creator.demand.errClosed', 'Набор на это задание уже закрыт.')
      : error.message.includes('not a claimed creator') ? tr('creator.demand.errUnclaimed', 'Откликаться можно после подтверждения аккаунта в профиле.')
      : tr('creator.demand.errApply', 'Заявка не отправилась. Проверьте интернет и попробуйте ещё раз.')); return; }
    setApplied(true); setShowForm(false);
    invalidate(['creator-campaign-feed']);
  };

  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div style={{ marginTop: 18 }}>
      <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
  const Chip = ({ children }: { children: ReactNode }) => (
    <span style={{ color: '#bcd', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12, padding: '4px 10px' }}>{children}</span>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0b0d12', zIndex: 20, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {c?.cover_image_url && <img src={c.cover_image_url} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', objectPosition: `50% ${c.cover_focus_y ?? 50}%`, display: 'block' }} />}
        <div className="cr-page" style={{ paddingTop: 14, paddingBottom: 24 }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#6699ff', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', padding: '0 0 10px' }}>← {tr('creator.demand.back', 'Назад')}</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <h1 style={{ color: '#fff', fontSize: 21, fontWeight: 700, lineHeight: 1.2, margin: 0 }}>{row.title}</h1>
            {(row.category_ru || row.category_en) && (
              <span style={{ flexShrink: 0, color: '#f5a9c8', background: 'rgba(245,169,200,0.12)', borderRadius: 12, fontSize: 12, padding: '4px 11px', whiteSpace: 'nowrap' }}>
                {i18n.language === 'en' ? (row.category_en || row.category_ru) : (row.category_ru || row.category_en)}
              </span>
            )}
          </div>
          {(brand?.name || row.brand_name) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8 }}>
              {(brand?.logo_url || row.brand_logo_url) && <img src={(brand?.logo_url || row.brand_logo_url)!} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />}
              <span style={{ color: 'var(--muted)', fontSize: 13.5 }}>{brand?.name || row.brand_name}</span>
            </div>
          )}

          {/* stat card */}
          <div style={{ display: 'flex', marginTop: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 6px' }}>
            {[
              { label: tr('creator.demand.budget', 'Бюджет'), value: compLabel(row) },
              { label: tr('creator.demand.deadline', 'Актуально до'), value: fmtDate(row.apply_deadline) || '—' },
              { label: tr('creator.demand.maxLabel', 'Макс. инфл.'), value: String(row.max_creators) },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', padding: '0 6px', borderLeft: i ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 600, lineHeight: 1.25 }}>{s.value}</div>
                <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {q.isLoading ? (
            <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 24 }}>…</div>
          ) : c ? (
            <>
              {dels.length > 0 && (
                <Section title={tr('creator.demand.deliverables', 'Что нужно снять')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {dels.map((d, i) => (
                      <Chip key={i}>{d.quantity}× {PLATFORM_RU[d.platform] || d.platform}{d.notes ? ` · ${d.notes}` : ''}</Chip>
                    ))}
                  </div>
                </Section>
              )}

              {c.description && (
                <Section title={tr('creator.demand.description', 'Описание')}>
                  <div style={{ color: '#bcc', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{c.description}</div>
                </Section>
              )}
              {c.requirements && (
                <Section title={tr('creator.demand.requirements', 'Требования и условия')}>
                  <div style={{ color: '#bcc', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{c.requirements}</div>
                </Section>
              )}
              {c.nice_to_have && (
                <Section title={tr('creator.demand.nice', 'Будет преимуществом')}>
                  <div style={{ color: '#bcc', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{c.nice_to_have}</div>
                </Section>
              )}
              {c.barter_description && (
                <Section title={tr('creator.demand.barterWhat', 'Что по бартеру')}>
                  <div style={{ color: '#bcc', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{c.barter_description}</div>
                </Section>
              )}

              <Section title={tr('creator.demand.details', 'Детали')}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {c.audience_geo && <Chip>{tr('creator.demand.geo', 'Гео')}: {c.audience_geo}</Chip>}
                  {(c.audience_age_min || c.audience_age_max) && <Chip>{tr('creator.demand.age', 'Возраст')}: {c.audience_age_min ?? ''}{c.audience_age_max ? `–${c.audience_age_max}` : '+'}</Chip>}
                  {c.min_followers && <Chip>{tr('creator.demand.minFollowers', 'от {{n}} подписчиков', { n: fmtK(c.min_followers) })}</Chip>}
                  {langLabel && <Chip>{tr('creator.demand.lang', 'Язык')}: {langLabel}</Chip>}
                  {c.on_site_required && <Chip>{tr('creator.demand.onsite', 'Нужно приехать')}{c.on_site_location ? ` · ${c.on_site_location}` : ''}</Chip>}
                  {usageLabel && <Chip>{usageLabel}</Chip>}
                  {c.content_deadline && <Chip>{tr('creator.demand.postBy', 'Публикация до')} {fmtDate(c.content_deadline)}</Chip>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {c.require_vertical && <Chip>9:16</Chip>}
                  {c.require_hd && <Chip>Full HD+</Chip>}
                  {c.no_competitor_logos && <Chip>{tr('creator.demand.noLogos', 'Без логотипов конкурентов')}</Chip>}
                  {c.must_tag_brand && <Chip>{tr('creator.demand.tagBrand', 'Отметить бренд')}</Chip>}
                </div>
              </Section>
            </>
          ) : (
            <div style={{ color: '#e74c3c', fontSize: 13, padding: 16 }}>{tr('creator.demand.loadErr', 'Не удалось загрузить задание.')}</div>
          )}
        </div>
      </div>

      {/* sticky CTA */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(11,13,18,0.98)', padding: '12px 16px calc(12px + env(safe-area-inset-bottom))' }}>
        {err && <div role="alert" style={{ color: 'var(--danger-text)', fontSize: 13, marginBottom: 8 }}>{err}</div>}
        {applied ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ color: '#8bd450', fontSize: 14, fontWeight: 600 }}>{tr('creator.demand.appliedFull', 'Заявка отправлена')}</span>
            <button onClick={onBack} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--muted)', borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{tr('creator.demand.back', 'Назад')}</button>
          </div>
        ) : showForm ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <textarea value={pitch} onChange={(e) => setPitch(e.target.value)} rows={3} aria-label={tr('creator.demand.pitchLabel', 'Почему вы подходите') as string}
              placeholder={tr('creator.demand.pitchPh', 'Коротко: почему вы подходите (необязательно)') as string}
              style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: 'var(--text)', fontSize: 13.5, padding: '9px 11px', fontFamily: 'inherit', resize: 'vertical' }} />
            {row.compensation_type !== 'barter' && (
              <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="numeric" aria-label={tr('creator.demand.priceLabel', 'Ваша цена') as string}
                placeholder={tr('creator.demand.pricePh', 'Ваша цена, {{cur}} (необязательно)', { cur: row.currency === 'KGS' ? 'сом' : row.currency }) as string}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: 'var(--text)', fontSize: 13.5, padding: '10px 11px', fontFamily: 'inherit' }} />
            )}
            <button onClick={submit} disabled={busy}
              style={{ background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: 10, padding: '13px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>
              {busy ? '…' : tr('creator.demand.send', 'Отправить заявку')}
            </button>
          </div>
        ) : !verified ? (
          // A brand answers the account behind the application — so it has to be proven first.
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 }}>
              {tr('creator.demand.verifyToApply', 'Чтобы откликнуться, подтвердите, что @{{h}} — ваш аккаунт. Это один комментарий в Instagram.', { h: me?.handle ?? '' })}
            </div>
            <button type="button" onClick={() => navigate('/provider/prices#verify')}
              style={{ width: '100%', background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: 10, padding: '14px 0', fontSize: 15.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {tr('creator.verify.ctaLong', 'Подтвердить аккаунт')}
            </button>
          </div>
        ) : (
          <button onClick={() => setShowForm(true)}
            style={{ width: '100%', background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: 10, padding: '14px 0', fontSize: 15.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {tr('creator.demand.apply', 'Податься')}
          </button>
        )}
      </div>
    </div>
  );
}
